import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Alert,
  Chip,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  SelectChangeEvent,
  Grid,
} from '@mui/material';
import {
  TrendingUp,
  TrendingDown,
  Schedule,
  People,
  Info,
  Warning,
  CheckCircle,
} from '@mui/icons-material';
import {
  LineChart,
  Line,
  AreaChart,
  Area,
  BarChart,
  Bar,
  ComposedChart,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip as RechartsTooltip,
  Legend,
  ResponsiveContainer,
  ReferenceLine,
  Scatter,
  ScatterChart,
  ZAxis,
} from 'recharts';
import { collection, query, where, getDocs, orderBy, limit, Timestamp, doc, getDoc } from 'firebase/firestore';
import { db } from '../../services/firebase';
import suburbsData from '../../data/suburbs.json';

// Type definition for suburbs data
interface SuburbEntry {
  suburb: string;
  postcode: number | string;
  state?: string;
}

interface SuburbsDataStructure {
  data: SuburbEntry[];
}

// Build postcode to suburb lookup map from JSON data
const postcodeMap = new Map<string, string>();
const typedSuburbsData = suburbsData as SuburbsDataStructure;

typedSuburbsData.data.forEach((entry: SuburbEntry) => {
  const postcode = String(entry.postcode);
  if (!postcodeMap.has(postcode)) {
    postcodeMap.set(postcode, entry.suburb);
  }
});

const getSuburbFromPostcode = (postcode: string): string | null => {
  return postcodeMap.get(postcode) || null;
};

// ─── Chart theme ─────────────────────────────────────────────────────────────
const GRID_STYLE = { stroke: '#eef0f5', strokeDasharray: '4 4' };
const AXIS_STYLE = {
  tick: { fill: '#8a94a6', fontSize: 11 },
  axisLine: { stroke: '#e8eaf0' },
  tickLine: false,
};
const TOOLTIP_STYLE = {
  contentStyle: {
    background: '#fff', border: '1px solid #e8eaf0', borderRadius: 10,
    boxShadow: '0 4px 16px rgba(0,0,0,0.08)', fontSize: 12, padding: '10px 14px',
  },
  labelStyle: { fontWeight: 600, color: '#1a1d23', marginBottom: 4 },
};
const TOGGLE_STYLES = {
  '& .MuiToggleButton-root': {
    border: '1px solid #e0e3ea', px: 1.75, py: 0.5,
    fontSize: '0.78rem', fontWeight: 500, color: '#8a94a6', textTransform: 'none',
    '&.Mui-selected': { background: '#6c63ff', color: '#fff', borderColor: '#6c63ff', '&:hover': { background: '#5b52e0' } },
  },
};

interface BestTimeCache {
  id: string;
  venue_id: string;
  cached_at: Timestamp;
  expires_at: Timestamp;
  hour_key: string;
  current_popularity: number;
  busyness_data: {
    status: string;
    analysis: {
      venue_forecasted_busyness: number | null;
      venue_live_busyness: number | null;
      venue_live_forecasted_delta: number | null;
      venue_forecast_busyness_available: boolean;
      venue_live_busyness_available: boolean;
      hour_start: number;
      hour_end: number;
      hour_start_12: string;
      hour_end_12: string;
    };
    venue_info: {
      venue_name: string;
      venue_address: string;
      venue_current_localtime: string;
      rating?: number;
      reviews?: number;
      venue_open?: string;
    };
  };
}

interface BusynessDataPoint {
  timestamp: string;
  time: string;
  venue_name: string;
  forecasted: number | null;
  live: number | null;
  delta: number | null;
  hasLive: boolean;
  hasForecast: boolean;
}

interface VenueBusynessStats {
  venue_name: string;
  venue_id: string;
  total_checks: number;
  avg_forecasted: number;
  avg_live: number;
  avg_delta: number;
  accuracy_rate: number;
  data_points: BusynessDataPoint[];
}

type ChartType = 'line' | 'area' | 'comparison' | 'scatter';
type TimeRange = 'day' | 'week' | 'month' | 'all';

// Helper function to format X-axis labels based on time range
const formatXAxisLabel = (timestamp: string, timeRange: TimeRange): string => {
  const date = new Date(timestamp);
  
  switch (timeRange) {
    case 'day':
      return date.toLocaleTimeString('en-US', { 
        hour: 'numeric', 
        minute: '2-digit',
        hour12: true 
      });
    case 'week':
      return date.toLocaleDateString('en-US', { 
        weekday: 'short',
        hour: 'numeric',
        hour12: true
      });
    case 'month':
      return date.toLocaleDateString('en-US', { 
        month: 'short',
        day: 'numeric'
      });
    case 'all':
      return date.toLocaleDateString('en-US', { 
        month: 'short',
        day: 'numeric'
      });
    default:
      return timestamp;
  }
};

const shouldShowDateMarker = (
  timestamp: string, 
  index: number, 
  data: BusynessDataPoint[], 
  timeRange: TimeRange
): boolean => {
  if (index === 0) return true;
  
  const currentDate = new Date(timestamp);
  const previousDate = new Date(data[index - 1].timestamp);
  
  switch (timeRange) {
    case 'day':
    case 'week':
      return currentDate.getDate() !== previousDate.getDate();
    case 'month':
    case 'all':
      return currentDate.getMonth() !== previousDate.getMonth();
    default:
      return false;
  }
};

const formatDateMarker = (timestamp: string, timeRange: TimeRange): string => {
  const date = new Date(timestamp);
  
  switch (timeRange) {
    case 'day':
    case 'week':
      return date.toLocaleDateString('en-US', { 
        weekday: 'long',
        month: 'short',
        day: 'numeric'
      });
    case 'month':
    case 'all':
      return date.toLocaleDateString('en-US', { 
        month: 'long',
        year: 'numeric'
      });
    default:
      return '';
  }
};

interface VenueAdminData {
  adminId: string;
  venuesOwned?: string[];
  name?: string;
  email?: string;
  company?: string;
  venueCount?: number;
  venueLimit?: number;
  subscription?: any;
  [key: string]: any; // Allow other properties
}

interface VenueAdminCapacityAnalyticsProps {
  venueAdminData: VenueAdminData;
}

const VenueAdminCapacityAnalytics: React.FC<VenueAdminCapacityAnalyticsProps> = ({ venueAdminData }) => {
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string>('');
  const [chartType, setChartType] = useState<ChartType>('comparison');
  const [timeRange, setTimeRange] = useState<TimeRange>('day');
  const [selectedVenue, setSelectedVenue] = useState<string>('all');
  
  const [allData, setAllData] = useState<BusynessDataPoint[]>([]);
  const [venueStats, setVenueStats] = useState<VenueBusynessStats[]>([]);
  const [assignedVenues, setAssignedVenues] = useState<{ id: string; name: string; address: string; state: string; postcode: string; suburb: string }[]>([]);
  const [aggregateStats, setAggregateStats] = useState({
    total_entries: 0,
    entries_with_forecast: 0,
    entries_with_live: 0,
    entries_with_both: 0,
    avg_forecast_accuracy: 0,
    avg_forecasted_busyness: 0,
    avg_live_busyness: 0,
  });

  useEffect(() => {
    loadAssignedVenues();
  }, [venueAdminData]);

  useEffect(() => {
    if (assignedVenues.length > 0) {
      loadBestTimeData();
    }
  }, [timeRange, assignedVenues]);

  // Helper function to extract state, postcode, and suburb from address
  const parseAddress = (address: string): { state: string; postcode: string; suburb: string } => {
    if (!address) {
      return { state: 'Unknown', postcode: 'Unknown', suburb: 'Unknown' };
    }

    const postcodePattern = /\b(\d{4})\b/;
    const postcodeMatch = address.match(postcodePattern);
    
    if (!postcodeMatch) {
      console.warn('⚠️ No postcode found in address:', address);
      return { state: 'Unknown', postcode: 'Unknown', suburb: 'Unknown' };
    }
    
    const postcode = postcodeMatch[1];
    const postcodeIndex = postcodeMatch.index!;
    const beforePostcode = address.substring(0, postcodeIndex).trim();
    const words = beforePostcode.split(/\s+/);
    const lastWord = words[words.length - 1];
    
    const statePattern = /^(NSW|VIC|QLD|SA|WA|TAS|NT|ACT)$/i;
    const state = statePattern.test(lastWord) ? lastWord.toUpperCase() : 'Unknown';
    
    let suburb = 'Unknown';
    try {
      suburb = getSuburbFromPostcode(postcode) || 'Unknown';
    } catch (error) {
      console.warn('⚠️ Could not find suburb for postcode:', postcode);
    }
    
    return { state, postcode, suburb };
  };

  // Load venues assigned to this venue admin
  const loadAssignedVenues = async () => {
    try {
      if (!venueAdminData?.adminId) {
        console.warn('⚠️ No venue admin data provided');
        setError('Venue admin data not available');
        setLoading(false);
        return;
      }

      console.log(`🔍 Loading venues for admin: ${venueAdminData.adminId}`);
      
      const venuesOwnedRaw = venueAdminData.venuesOwned || [];

      if (venuesOwnedRaw.length === 0) {
        console.warn('⚠️ No venues found in venuesOwned array');
        setError('No venues assigned to your account');
        setLoading(false);
        return;
      }

      // ✅ DEDUPLICATE venue IDs before loading
      const venuesOwned = Array.from(new Set(venuesOwnedRaw));

      console.log(`📋 Found ${venuesOwnedRaw.length} venue IDs in venuesOwned array (${venuesOwned.length} unique):`, venuesOwned);
      
      if (venuesOwnedRaw.length !== venuesOwned.length) {
        console.warn(`⚠️ Removed ${venuesOwnedRaw.length - venuesOwned.length} duplicate venue IDs`);
      }

      // Now fetch the actual venue documents using doc references
      const venues = [];
      
      for (const venueId of venuesOwned) {
        try {
          const venueDocRef = doc(db, 'venues', venueId);
          const venueSnapshot = await getDoc(venueDocRef);
          
          if (venueSnapshot.exists()) {
            const data = venueSnapshot.data();
            const { state, postcode, suburb } = parseAddress(data.address || '');
            
            venues.push({
              id: venueSnapshot.id,
              name: data.name || 'Unknown Venue',
              address: data.address || '',
              state,
              postcode,
              suburb
            });
            console.log(`✅ Loaded venue: ${data.name}`);
          } else {
            console.warn(`⚠️ Venue document not found: ${venueId}`);
          }
        } catch (err) {
          console.warn(`⚠️ Could not load venue ${venueId}:`, err);
        }
      }

      if (venues.length === 0) {
        console.warn('⚠️ No valid venues could be loaded');
        setError('No venues could be loaded');
        setLoading(false);
        return;
      }

      console.log(`✅ Successfully loaded ${venues.length} assigned venues`);
      setAssignedVenues(venues);
      
    } catch (err: any) {
      console.error('❌ Error loading assigned venues:', err);
      setError(`Failed to load venues: ${err.message}`);
      setLoading(false);
    }
  };

  const loadBestTimeData = async () => {
    try {
      setLoading(true);
      setError('');

      if (assignedVenues.length === 0) {
        console.warn('⚠️ No assigned venues to load data for');
        setLoading(false);
        return;
      }

      // Calculate date range
      const endDate = new Date();
      const startDate = new Date();
      
      switch (timeRange) {
        case 'day':
          startDate.setDate(startDate.getDate() - 1);
          break;
        case 'week':
          startDate.setDate(startDate.getDate() - 7);
          break;
        case 'month':
          startDate.setDate(startDate.getDate() - 30);
          break;
        case 'all':
          startDate.setFullYear(startDate.getFullYear() - 1);
          break;
      }

      console.log(`📅 Loading capacity data from ${startDate.toLocaleDateString()} to ${endDate.toLocaleDateString()}`);

      // Get venue IDs to filter by
      const venueIds = assignedVenues.map(v => v.id);
      
      // Query besttime_cache collection for assigned venues only
      // Note: Firestore 'in' queries support up to 10 items, so we may need to batch
      const batchSize = 10;
      let allCacheData: BestTimeCache[] = [];

      for (let i = 0; i < venueIds.length; i += batchSize) {
        const batchVenueIds = venueIds.slice(i, i + batchSize);
        
        const cacheQuery = query(
          collection(db, 'besttime_cache'),
          where('venue_id', 'in', batchVenueIds),
          where('cached_at', '>=', Timestamp.fromDate(startDate)),
          where('cached_at', '<=', Timestamp.fromDate(endDate)),
          orderBy('cached_at', 'desc')
        );

        const snapshot = await getDocs(cacheQuery);
        const batchData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        } as BestTimeCache));
        
        allCacheData = [...allCacheData, ...batchData];
      }

      console.log(`📊 Found ${allCacheData.length} besttime_cache entries for assigned venues`);

      if (allCacheData.length === 0) {
        console.warn('⚠️ No capacity data found for the selected time range');
        setAllData([]);
        setVenueStats([]);
        setAggregateStats({
          total_entries: 0,
          entries_with_forecast: 0,
          entries_with_live: 0,
          entries_with_both: 0,
          avg_forecast_accuracy: 0,
          avg_forecasted_busyness: 0,
          avg_live_busyness: 0,
        });
        setLoading(false);
        // Don't set error - just show empty state in chart
        return;
      }

      // Process the data
      const dataPoints: BusynessDataPoint[] = allCacheData.map(cache => {
        const cachedAt = cache.cached_at.toDate();
        const analysis = cache.busyness_data?.analysis || {};
        
        return {
          timestamp: cachedAt.toISOString(),
          time: cachedAt.toLocaleTimeString('en-AU', { 
            hour: '2-digit', 
            minute: '2-digit',
            hour12: true 
          }),
          venue_name: cache.busyness_data?.venue_info?.venue_name || 'Unknown',
          forecasted: analysis.venue_forecasted_busyness,
          live: analysis.venue_live_busyness,
          delta: analysis.venue_live_forecasted_delta,
          hasLive: analysis.venue_live_busyness !== null && analysis.venue_live_busyness !== undefined,
          hasForecast: analysis.venue_forecasted_busyness !== null && analysis.venue_forecasted_busyness !== undefined,
        };
      });

      // Sort by timestamp
      dataPoints.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());

      // Calculate venue-specific statistics
      const venueMap = new Map<string, BusynessDataPoint[]>();
      dataPoints.forEach(point => {
        if (!venueMap.has(point.venue_name)) {
          venueMap.set(point.venue_name, []);
        }
        venueMap.get(point.venue_name)!.push(point);
      });

      const stats: VenueBusynessStats[] = Array.from(venueMap.entries()).map(([venueName, points]) => {
        const venue = assignedVenues.find(v => v.name === venueName);
        const pointsWithBoth = points.filter(p => p.hasLive && p.hasForecast);
        
        const avgForecasted = points
          .filter(p => p.hasForecast)
          .reduce((sum, p) => sum + (p.forecasted || 0), 0) / points.filter(p => p.hasForecast).length || 0;
        
        const avgLive = points
          .filter(p => p.hasLive)
          .reduce((sum, p) => sum + (p.live || 0), 0) / points.filter(p => p.hasLive).length || 0;
        
        const avgDelta = pointsWithBoth.length > 0
          ? pointsWithBoth.reduce((sum, p) => sum + Math.abs(p.delta || 0), 0) / pointsWithBoth.length
          : 0;
        
        const accuracyRate = pointsWithBoth.length > 0
          ? 100 - avgDelta
          : 0;

        return {
          venue_name: venueName,
          venue_id: venue?.id || '',
          total_checks: points.length,
          avg_forecasted: avgForecasted,
          avg_live: avgLive,
          avg_delta: avgDelta,
          accuracy_rate: Math.max(0, accuracyRate),
          data_points: points,
        };
      });

      // Sort by total checks descending
      stats.sort((a, b) => b.total_checks - a.total_checks);

      // Calculate aggregate statistics
      const totalEntries = dataPoints.length;
      const entriesWithForecast = dataPoints.filter(d => d.hasForecast).length;
      const entriesWithLive = dataPoints.filter(d => d.hasLive).length;
      const entriesWithBoth = dataPoints.filter(d => d.hasLive && d.hasForecast).length;
      
      const avgForecastedBusyness = entriesWithForecast > 0
        ? dataPoints.filter(d => d.hasForecast).reduce((sum, d) => sum + (d.forecasted || 0), 0) / entriesWithForecast
        : 0;
      
      const avgLiveBusyness = entriesWithLive > 0
        ? dataPoints.filter(d => d.hasLive).reduce((sum, d) => sum + (d.live || 0), 0) / entriesWithLive
        : 0;
      
      const avgForecastAccuracy = entriesWithBoth > 0
        ? 100 - (dataPoints.filter(d => d.hasLive && d.hasForecast)
            .reduce((sum, d) => sum + Math.abs(d.delta || 0), 0) / entriesWithBoth)
        : 0;

      setAllData(dataPoints);
      setVenueStats(stats);
      setAggregateStats({
        total_entries: totalEntries,
        entries_with_forecast: entriesWithForecast,
        entries_with_live: entriesWithLive,
        entries_with_both: entriesWithBoth,
        avg_forecast_accuracy: Math.max(0, avgForecastAccuracy),
        avg_forecasted_busyness: avgForecastedBusyness,
        avg_live_busyness: avgLiveBusyness,
      });

      console.log('✅ Capacity data loaded successfully');
      console.log(`📈 Stats: ${totalEntries} total, ${entriesWithForecast} forecasts, ${entriesWithLive} live`);

    } catch (err: any) {
      console.error('❌ Error loading capacity data:', err);
      setError(`Failed to load capacity data: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleChartTypeChange = (event: React.MouseEvent<HTMLElement>, newType: ChartType | null) => {
    if (newType !== null) {
      setChartType(newType);
    }
  };

  const handleTimeRangeChange = (event: React.MouseEvent<HTMLElement>, newRange: TimeRange | null) => {
    if (newRange !== null) {
      setTimeRange(newRange);
    }
  };

  const handleVenueChange = (event: SelectChangeEvent<string>) => {
    setSelectedVenue(event.target.value);
  };

  // Filter data based on selected venue
  const filteredData = selectedVenue === 'all' 
    ? allData 
    : allData.filter(d => d.venue_name === selectedVenue);

  const filteredVenueStats = selectedVenue === 'all'
    ? venueStats
    : venueStats.filter(v => v.venue_name === selectedVenue);

  const CustomTooltip = ({ active, payload }: any) => {
    if (!active || !payload?.length) return null;
    const d = payload[0].payload;
    return (
      <Box sx={{ background: '#fff', border: '1px solid #e8eaf0', borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', p: 1.75, fontSize: 12 }}>
        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1d23', mb: 0.5 }}>{d.venue_name}</Typography>
        <Typography sx={{ fontSize: '0.72rem', color: '#8a94a6', mb: 0.75 }}>{d.time}</Typography>
        {d.hasForecast && <Typography sx={{ fontSize: '0.78rem', color: '#6c63ff' }}>Forecasted: {d.forecasted}%</Typography>}
        {d.hasLive && <Typography sx={{ fontSize: '0.78rem', color: '#16a76a' }}>Live: {d.live}%</Typography>}
        {d.hasLive && d.hasForecast && (
          <Typography sx={{ fontSize: '0.78rem', color: d.delta > 0 ? '#f59e0b' : '#14b8a6' }}>
            Delta: {d.delta > 0 ? '+' : ''}{d.delta?.toFixed(1)}%
          </Typography>
        )}
      </Box>
    );
  };

  const dateMarkers = filteredData
    .map((dp, i) => shouldShowDateMarker(dp.timestamp, i, filteredData, timeRange) ? (
      <ReferenceLine key={`m-${i}`} x={dp.timestamp} stroke="#e8eaf0" strokeDasharray="4 4"
        label={{ value: formatDateMarker(dp.timestamp, timeRange), position: 'top', fill: '#8a94a6', fontSize: 10 }} />
    ) : null);

  const commonXAxis = (
    <XAxis dataKey="timestamp" {...AXIS_STYLE} interval="preserveStartEnd"
      tickFormatter={(v) => formatXAxisLabel(v, timeRange)} />
  );
  const commonYAxis = (
    <YAxis {...AXIS_STYLE} width={42} domain={[0, 100]} tickFormatter={(v) => `${v}%`} />
  );

  const renderChart = () => {
    switch (chartType) {
      case 'line':
        return (
          <LineChart data={filteredData}>
            <CartesianGrid {...GRID_STYLE} vertical={false} />
            {commonXAxis}{commonYAxis}
            {dateMarkers}
            <RechartsTooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            <Line type="monotone" dataKey="forecasted" stroke="#6c63ff" strokeWidth={2} name="Forecasted" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} connectNulls />
            <Line type="monotone" dataKey="live" stroke="#16a76a" strokeWidth={2} name="Live" dot={false} activeDot={{ r: 4, strokeWidth: 0 }} connectNulls />
          </LineChart>
        );

      case 'area':
        return (
          <AreaChart data={filteredData}>
            <defs>
              <linearGradient id="capForecast" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#6c63ff" stopOpacity={0.15}/><stop offset="100%" stopColor="#6c63ff" stopOpacity={0}/>
              </linearGradient>
              <linearGradient id="capLive" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#16a76a" stopOpacity={0.15}/><stop offset="100%" stopColor="#16a76a" stopOpacity={0}/>
              </linearGradient>
            </defs>
            <CartesianGrid {...GRID_STYLE} vertical={false} />
            {commonXAxis}{commonYAxis}
            {dateMarkers}
            <RechartsTooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            <Area type="monotone" dataKey="forecasted" stroke="#6c63ff" strokeWidth={2} fill="url(#capForecast)" name="Forecasted" dot={false} connectNulls />
            <Area type="monotone" dataKey="live" stroke="#16a76a" strokeWidth={2} fill="url(#capLive)" name="Live" dot={false} connectNulls />
          </AreaChart>
        );

      case 'comparison':
        return (
          <ComposedChart data={filteredData}>
            <CartesianGrid {...GRID_STYLE} vertical={false} />
            {commonXAxis}{commonYAxis}
            {dateMarkers}
            <RechartsTooltip content={<CustomTooltip />} />
            <Legend wrapperStyle={{ fontSize: 12, paddingTop: 8 }} />
            <Bar dataKey="forecasted" fill="#6c63ff" fillOpacity={0.55} name="Forecasted" radius={[2, 2, 0, 0]} />
            <Bar dataKey="live" fill="#16a76a" fillOpacity={0.55} name="Live" radius={[2, 2, 0, 0]} />
            <Line type="monotone" dataKey="delta" stroke="#f59e0b" strokeWidth={2} name="Delta" dot={false} connectNulls />
          </ComposedChart>
        );

      case 'scatter': {
        const scatterData = filteredData.filter(d => d.hasLive && d.hasForecast);
        return (
          <ScatterChart>
            <CartesianGrid {...GRID_STYLE} />
            <XAxis type="number" dataKey="forecasted" name="Forecasted" domain={[0, 100]} {...AXIS_STYLE}
              label={{ value: 'Forecasted (%)', position: 'bottom', fill: '#8a94a6', fontSize: 11 }} />
            <YAxis type="number" dataKey="live" name="Live" domain={[0, 100]} {...AXIS_STYLE} width={42}
              label={{ value: 'Live (%)', angle: -90, position: 'insideLeft', fill: '#8a94a6', fontSize: 11 }} />
            <ZAxis range={[40, 160]} />
            <RechartsTooltip content={({ active, payload }: any) => {
              if (!active || !payload?.length) return null;
              const d = payload[0].payload;
              return (
                <Box sx={{ background: '#fff', border: '1px solid #e8eaf0', borderRadius: 2, boxShadow: '0 4px 16px rgba(0,0,0,0.08)', p: 1.75, fontSize: 12 }}>
                  <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: '#1a1d23', mb: 0.5 }}>{d.venue_name}</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#6c63ff' }}>Forecasted: {d.forecasted}%</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#16a76a' }}>Live: {d.live}%</Typography>
                  <Typography sx={{ fontSize: '0.78rem', color: '#8a94a6' }}>Accuracy: {(100 - Math.abs(d.delta)).toFixed(1)}%</Typography>
                </Box>
              );
            }} cursor={{ strokeDasharray: '3 3' }} />
            <ReferenceLine segment={[{ x: 0, y: 0 }, { x: 100, y: 100 }]} stroke="#e8eaf0" strokeDasharray="4 4"
              label={{ value: 'Perfect', fill: '#8a94a6', fontSize: 11 }} />
            <Scatter name="Forecast vs Live" data={scatterData} fill="#6c63ff" fillOpacity={0.55} />
          </ScatterChart>
        );
      }

      default:
        return null;
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight={400}>
        <CircularProgress size={40} sx={{ color: '#6c63ff' }} />
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 2.5, borderRadius: 2.5, border: '1px solid #e8eaf0', background: '#fff5f5' }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>
      </Box>
    );
  }

  return (
    <Box sx={{ background: '#f8f9fc' }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={3} flexWrap="wrap" gap={2}>
        <Box>
          <Typography variant="h5" fontWeight={700} color="#1a1d23">Venue Capacity Analytics</Typography>
          <Typography variant="body2" color="text.secondary">Forecasted vs live busyness data for your venues</Typography>
        </Box>
        {/* Controls */}
        <Box display="flex" gap={1.5} flexWrap="wrap" alignItems="center">
          <ToggleButtonGroup value={timeRange} exclusive onChange={handleTimeRangeChange} size="small" sx={TOGGLE_STYLES}>
            <ToggleButton value="day">24H</ToggleButton>
            <ToggleButton value="week">7 Days</ToggleButton>
            <ToggleButton value="month">30 Days</ToggleButton>
            <ToggleButton value="all">All Time</ToggleButton>
          </ToggleButtonGroup>
          <ToggleButtonGroup value={chartType} exclusive onChange={handleChartTypeChange} size="small" sx={TOGGLE_STYLES}>
            <ToggleButton value="line">Line</ToggleButton>
            <ToggleButton value="area">Area</ToggleButton>
            <ToggleButton value="comparison">Compare</ToggleButton>
            <ToggleButton value="scatter">Scatter</ToggleButton>
          </ToggleButtonGroup>
          {assignedVenues.length > 1 && (
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel sx={{ fontSize: '0.8rem' }}>Venue</InputLabel>
              <Select value={selectedVenue} label="Venue" onChange={handleVenueChange} sx={{ fontSize: '0.8rem', borderRadius: 1.5 }}>
                <MenuItem value="all">
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography variant="body2">All Your Venues</Typography>
                    <Chip label={assignedVenues.length} size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                  </Box>
                </MenuItem>
                {assignedVenues.map(venue => {
                  const vStats = venueStats.find(v => v.venue_id === venue.id);
                  return (
                    <MenuItem key={venue.id} value={venue.name}>
                      <Box>
                        <Typography variant="body2">{venue.name}</Typography>
                        <Typography variant="caption" color="text.secondary">
                          {venue.suburb !== 'Unknown' ? `${venue.suburb} (${venue.postcode})` : venue.postcode}, {venue.state}
                          {vStats && ` · ${vStats.total_checks} checks`}
                        </Typography>
                      </Box>
                    </MenuItem>
                  );
                })}
              </Select>
            </FormControl>
          )}
          {selectedVenue !== 'all' && (
            <Chip label={`Venue: ${selectedVenue}`} onDelete={() => setSelectedVenue('all')}
              color="primary" size="small" sx={{ height: 24, fontSize: '0.72rem' }} />
          )}
        </Box>
      </Box>

      {/* Stat Cards */}
      <Grid container spacing={2.5} mb={3}>
        {[
          { label: 'Your Venues', value: assignedVenues.length, sub: 'Monitored venues', icon: <People />, accent: '#6c63ff', bgTint: '#f5f4ff' },
          { label: 'Data Points', value: aggregateStats.total_entries, sub: `${aggregateStats.entries_with_both} with both`, icon: <Schedule />, accent: '#3b82f6', bgTint: '#eff6ff' },
          { label: 'Avg Forecasted', value: `${aggregateStats.avg_forecasted_busyness.toFixed(0)}%`, sub: 'Predicted busyness', icon: <TrendingUp />, accent: '#f59e0b', bgTint: '#fffbf0' },
          {
            label: 'Forecast Accuracy',
            value: `${aggregateStats.avg_forecast_accuracy.toFixed(1)}%`,
            sub: aggregateStats.avg_forecast_accuracy > 80 ? 'Highly accurate' : aggregateStats.avg_forecast_accuracy > 60 ? 'Moderate accuracy' : 'Low accuracy',
            subColor: aggregateStats.avg_forecast_accuracy > 80 ? '#16a76a' : aggregateStats.avg_forecast_accuracy > 60 ? '#f59e0b' : '#ef4444',
            icon: <CheckCircle />, accent: '#16a76a', bgTint: '#f0faf5',
          },
        ].map((card: any) => (
          <Grid key={card.label} size={{ xs: 12, sm: 6, md: 3 }}>
            <Box sx={{
              background: '#fff', borderRadius: 2.5, p: 2.5,
              border: '1px solid #e8eaf0', borderLeft: `4px solid ${card.accent}`,
              boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
              transition: 'transform 0.15s, box-shadow 0.15s',
              '&:hover': { transform: 'translateY(-2px)', boxShadow: '0 6px 20px rgba(0,0,0,0.09)' },
            }}>
              <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                <Box>
                  <Typography sx={{ fontSize: '0.72rem', fontWeight: 600, letterSpacing: '0.07em', textTransform: 'uppercase', color: '#8a94a6', mb: 0.75 }}>
                    {card.label}
                  </Typography>
                  <Typography sx={{ fontSize: '1.9rem', fontWeight: 700, color: '#1a1d23', lineHeight: 1, mb: 0.5 }}>
                    {card.value}
                  </Typography>
                  <Typography sx={{ fontSize: '0.75rem', color: card.subColor || '#8a94a6' }}>{card.sub}</Typography>
                </Box>
                <Box sx={{ width: 40, height: 40, borderRadius: 2, background: card.bgTint, color: card.accent, display: 'flex', alignItems: 'center', justifyContent: 'center', '& svg': { fontSize: 20 } }}>
                  {card.icon}
                </Box>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      {/* Main Chart */}
      <Card sx={{ mb: 3, borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <CardContent sx={{ p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2.5}>
            <Box>
              <Typography variant="h6" fontWeight={600} color="#1a1d23">Forecasted vs Live Capacity</Typography>
              <Typography variant="body2" color="text.secondary">Busyness levels across your venues</Typography>
            </Box>
            <Box display="flex" gap={1}>
              <Chip size="small" label={`${filteredData.filter(d => d.hasForecast).length} forecasts`}
                sx={{ height: 22, fontSize: '0.7rem', background: '#f5f4ff', color: '#6c63ff', border: '1px solid #e0dbff' }} />
              <Chip size="small" label={`${filteredData.filter(d => d.hasLive).length} live`}
                sx={{ height: 22, fontSize: '0.7rem', background: '#f0faf5', color: '#16a76a', border: '1px solid #bbf0d9' }} />
            </Box>
          </Box>

          {filteredData.length === 0 ? (
            <Box display="flex" justifyContent="center" alignItems="center" height={360} flexDirection="column" gap={1.5}>
              <Info sx={{ fontSize: 40, color: '#d1d5e0' }} />
              <Typography variant="h6" color="text.secondary" fontWeight={500}>No capacity data for this range</Typography>
              <Typography variant="body2" color="text.secondary" textAlign="center" maxWidth={480}>
                Capacity data is collected when users view your venue in JamApp. Try a wider time range, or check back once your venue has been viewed.
              </Typography>
            </Box>
          ) : (
            <ResponsiveContainer width="100%" height={380}>
              {renderChart() || <></>}
            </ResponsiveContainer>
          )}

          {chartType === 'scatter' && filteredData.length > 0 && (
            <Box mt={2} px={2} py={1.25} sx={{ background: '#f5f4ff', borderRadius: 1.5, border: '1px solid #e0dbff' }}>
              <Typography sx={{ fontSize: '0.78rem', color: '#6c63ff', fontWeight: 500 }}>
                Points on the diagonal line indicate perfect forecast accuracy. Above the line means live busyness exceeded the forecast; below means it was lower.
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      {/* Venue Performance Table */}
      <Card sx={{ borderRadius: 2.5, border: '1px solid #e8eaf0', boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}>
        <CardContent sx={{ p: 3 }}>
          <Typography variant="h6" fontWeight={600} color="#1a1d23" gutterBottom>Venue Forecast Performance</Typography>
          {filteredVenueStats.length === 0 ? (
            <Box p={3} textAlign="center">
              <Typography variant="body2" color="text.secondary">No venue statistics available</Typography>
            </Box>
          ) : (
            <Box display="flex" flexDirection="column" gap={1.25} mt={1}>
              {filteredVenueStats.map((venue) => (
                <Box key={venue.venue_id} sx={{ p: 2, borderRadius: 2, background: '#f8f9fc', border: '1px solid #eef0f5' }}>
                  <Box display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={1}>
                    <Box>
                      <Typography variant="body2" fontWeight={600} color="#1a1d23">{venue.venue_name}</Typography>
                      <Typography variant="caption" color="text.secondary">{venue.total_checks} data points</Typography>
                    </Box>
                    <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
                      <Box textAlign="right">
                        <Typography sx={{ fontSize: '0.68rem', color: '#8a94a6' }}>Avg Forecast</Typography>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#6c63ff' }}>{venue.avg_forecasted.toFixed(0)}%</Typography>
                      </Box>
                      <Box textAlign="right">
                        <Typography sx={{ fontSize: '0.68rem', color: '#8a94a6' }}>Avg Live</Typography>
                        <Typography sx={{ fontSize: '0.82rem', fontWeight: 600, color: '#16a76a' }}>{venue.avg_live.toFixed(0)}%</Typography>
                      </Box>
                      <Box sx={{
                        px: 1.25, py: 0.5, borderRadius: 1.5,
                        background: venue.accuracy_rate > 80 ? '#f0faf5' : venue.accuracy_rate > 60 ? '#fffbf0' : '#fff5f5',
                        border: `1px solid ${venue.accuracy_rate > 80 ? '#bbf0d9' : venue.accuracy_rate > 60 ? '#fde68a' : '#fecaca'}`,
                      }}>
                        <Typography sx={{ fontSize: '0.78rem', fontWeight: 600, color: venue.accuracy_rate > 80 ? '#16a76a' : venue.accuracy_rate > 60 ? '#f59e0b' : '#ef4444' }}>
                          {venue.accuracy_rate.toFixed(1)}% accurate
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Box>
              ))}
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default VenueAdminCapacityAnalytics;